import gsap from 'gsap';
import { ControllerPage } from '../_app/cuchillo/pages/ControllerPage';
import { isDebug, isTouch } from '../_app/cuchillo/core/Basics';
import Default from './Default';
import Scene from '../3D/Scene';
import AudioController from '../layout/AudioController';
import Preloader from '../layout/Preloader';
import Wrap from '../layout/Wrap';
import HomeScene from '../3D/HomeScene';
import { Scroll } from '../_app/cuchillo/scroll/Scroll';
import Keyboard from '../_app/cuchillo/core/Keyboard';
import Header from '../layout/Header';
import WebGLSketch from '../_app/cuchillo/3D/WebGLSketch';
import DebugPane from '../3D/DebugPane';
import { GetBy } from '../_app/cuchillo/core/Element';
import { Metrics } from '../_app/cuchillo/core/Metrics';
import ScrollIndicator from '../layout/ScrollIndicator';

// Durada de l'scroll programàtic de la intro (emula l'easing/durada de la intro antiga del character).
const HOME_INTRO_DURATION = 3;

export default class Home extends Default {
  constructor() {
    super();
    HomeScene.init(this.container);
  }

  beforeShow() {
    // Home ara és una pàgina AMB scroll (abans NoScroll): no bloquegem l'scroll.
    // Seguim SENSE cridar super.beforeShow() perquè Default.beforeShow() faria
    // Scene.hideCharacter()/hideBGSphere() i la Home ha de mantenir el character visible.
    // wrapper/content = #Main/.wrap (no window): el document es queda congelat a
    // l'alçada del viewport (veure _general.scss, body.page-home) perquè iOS no
    // amagui/mostri la toolbar en fer scroll — l'scroll real el porta #Main.
    // syncTouch NOMÉS en touch: Lenis pren el control del gest i actualitza la
    // posició cada frame (el seu propi rAF), en lloc de dependre de l'event natiu
    // 'scroll' del wrapper — que a Safari/iOS es dispara amb menys freqüència que
    // el redibuixat real i provocava el "salt" cada N píxels als elements amb
    // --speed:-1 (transform recalculat tard i corregit de cop). A desktop es queda
    // false (comportament actual, sense tocar).
    // allowNestedScroll: sense això, syncTouch fa que Lenis robi (preventDefault)
    // qualsevol gest amb component vertical encara que hagi començat dins d'un altre
    // element amb scroll propi (p.ex. l'slider horitzontal de block_discovery_set) —
    // Lenis exposa aquesta opció exactament per detectar-ho i cedir-li el gest.
    Scroll.init({ wrapper: GetBy.id('Main'), content: GetBy.class('wrap')[0], syncTouch: isTouch, allowNestedScroll: true });
    Keyboard.mountPage(this.id);

    // Home: llums de producte frontals (X=0) + lookAt a cada tapon-single lateral.
    // La resta de pàgines mantenen la posició lateral OG.
    Scene.setLightsHome();

    if (this.isFirstTime) {
      Scene.start();
      WebGLSketch.start();
    }

  }

  show__effect(__call) {
    if (this.isFirstTime) {
      Scene.requestPrewarm();
      // Escalfa (compila) el shader del BGSphere ara, mentre l'usuari encara veu la capa
      // d'àudio — dona marge abans del moment crític de _playIntro() (evita el salt).
      Scene.warmBGSphere();
      if (isDebug) {
        AudioController.directHide();
        Preloader.directHide(() => {
          Wrap.show(() => { this.afterShow(); });
        });
        Header.show();
      } else {
        AudioController.showLayer(() => {
          Preloader.hide(() => {
            Wrap.show(() => { this.afterShow(); });
          });
        });
      }
    } else {
      Wrap.show(() => { this.afterShow(); });
    }
  }

  afterShow() {
    this._isActive = true;
    this._setupAnchors();

    // Sempre (primera vegada o no, debug o no): si venim d'una pàgina sense BGSphere
    // (Default.beforeShow el fa hideBGSphere), aquí es torna a mostrar. showBGSphere()
    // ja és idempotent (warm-up lazy + guard _bgVisible).
    Scene.showBGSphere();

    WebGLSketch.enableRaycast();
    Header.enablePointer(); // reactiva el header per si venia desactivat (animació general)
    Scroll.start();
    Scroll.show();
    ScrollIndicator.show(); // Home no crida super.afterShow() → cal explícitament

    // (llums de producte persistents per layer: no cal remuntar-les)

    // Intro: scroll programàtic (veure _playIntro).
    this._playIntro();
  }

  // Intro de la Home com a SCROLL PROGRAMÀTIC: mostra el character (opacitat) i fa avançar l'scroll
  // l'alçada del spacer amb l'easing de la intro antiga. Aquest scroll REAL fa que el spacer mogui
  // la posició del character (WidgetHomeSpacer) i que els textos (WidgetHomeText) es fonguin sols.
  // En acabar, l'usuari ja pot scrollar (el lock s'allibera).
  _playIntro() {
    Scene.introCharacter(); // reset: character a l'origen (lluny) + opacitat 0
    Scene.showCharacter();  // fade únic d'opacitat 0→1

    const spacer = GetBy.class('widget-home__spacer', this.container)[0];
    const target = spacer ? spacer.offsetTop + spacer.offsetHeight : Metrics.HEIGHT;

    this._introRunning = true;
    Scroll.lenis?.scrollTo(target, {
      duration: HOME_INTRO_DURATION,
      easing: gsap.parseEase('power3.inOut'),
      lock: true,
      force: true,
      onComplete: () => {
        this._introRunning = false;
        Scene.enableCharacterMouse(); // intro acabada → el parallax del mouse ja pot actuar
      },
    });
  }

  beforeHide() {
    // Si la intro (scroll programàtic) encara corre, la cancel·lem i alliberem el lock.
    if (this._introRunning) { Scroll.lenis?.scrollTo(Scroll.y, { immediate: true, lock: false }); this._introRunning = false; }
    // Hide "normal" de la pàgina (link de menú, etc.): els tapons marxen com han arribat.
    // Si el hide ve d'un click de tapon, onTaponClick ja gestiona el seu propi ordre/stagger
    // (_taponSelected==true) → no el repetim aquí.
    if (!HomeScene._taponSelected) HomeScene.hideTaponesAnimated();
    // Força la posició lateral OG per si sortim de la Home per un link del menú
    // (no per l'animació, que ja ho fa a onTaponClick).
    Scene.setLightsDefault();
    DebugPane.removeCharacter(); // desmunta els settings del character en sortir de la Home
    // No amaguem character ni BGSphere aquí: si anem a Products s'han de mantenir.
    // Les pàgines Default ja fan Scene.hideCharacter() + Scene.hideBGSphere() al seu beforeShow.
    super.beforeHide();
  }

  afterHide() {
    HomeScene.dispose();
    super.afterHide();
  }

  // La intro (_playIntro) fa un scroll programàtic d'un fold sencer: l'indicador
  // ha de sobreviure-la i amagar-se un 20% més enllà.
  get scrollIndicatorRatio() { return 1 + ScrollIndicator.HIDE_RATIO; }

  loop() {
    super.loop();
    HomeScene.loop();
  }

  resize(__w, __h) {
    super.resize(__w, __h);
    HomeScene.resize();
  }
}

ControllerPage._addPage('page-home', Home);
